/**
 * LocalTimePill — calm trip-local time chip (Wave 30 · Phase 7d).
 * ────────────────────────────────────────────────────────────────
 * Surfaces in the HeroZone when the trip is unveiled AND the traveler
 * is in `preparing` or `traveling` state. Auto-ticks every 30s.
 *
 *   Renders: ⏱ LOCAL · 07:42 AM · EST
 *
 * Suppresses when:
 *   - trip is veiled (would leak destination)
 *   - timezone is "UTC" (no meaningful local clock)
 */
import React from "react";
import { Clock } from "lucide-react";
import { useLocalTime, getTripTimezone } from "../../../../lib/timezone";

export default function LocalTimePill({ trip, veiled, state }) {
  const tz = getTripTimezone(trip);
  const { time, abbreviation } = useLocalTime(tz);

  // Veil-safe: never expose local time when the destination is masked.
  if (veiled) return null;
  // No-op when no timezone is known.
  if (!tz || tz === "UTC") return null;
  // Surface only when the time is relevant (within prep window or traveling).
  if (!["preparing", "traveling"].includes(state)) return null;
  if (!time) return null;

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm text-white/85 font-mono text-[10px] uppercase tracking-[0.22em]"
      data-testid="hero-local-time-pill"
      title={`Local time in ${tz}`}
    >
      <Clock className="size-3" strokeWidth={1.5} />
      <span>Local · {time}</span>
      {abbreviation ? <span className="opacity-60">· {abbreviation}</span> : null}
    </div>
  );
}
